/**
 * BreakevenMetrics Component
 *
 * Displays key breakeven metrics in a card grid layout:
 * - Breakeven (kg)
 * - Breakeven (GBP)
 * - Safety Margin %
 * - Performance %
 *
 * Part of US-P7-003: Breakeven Analysis
 */

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Target,
  PoundSterling,
  Shield,
  TrendingUp,
  AlertTriangle
} from 'lucide-react'

/**
 * Format number as currency
 *
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Format number with kg unit
 *
 * @param {number} value - Value in kg
 * @returns {string} Formatted string with kg unit
 */
function formatKg(value) {
  if (value === null || value === undefined) return 'N/A'
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}t`
  }
  return `${value.toFixed(1)} kg`
}

/**
 * Format percentage
 *
 * @param {number} value - Percentage value
 * @returns {string} Formatted percentage string
 */
function formatPercent(value) {
  if (value === null || value === undefined) return 'N/A'
  return `${value.toFixed(1)}%`
}

/**
 * Get colour classes based on safety margin
 */
function getSafetyColour(margin) {
  if (margin === null || margin === undefined) return 'text-neutral-600'
  if (margin > 30) return 'text-emerald-600'
  if (margin > 10) return 'text-amber-600'
  if (margin > 0) return 'text-orange-600'
  return 'text-red-600'
}

/**
 * Get colour classes based on performance
 */
function getPerformanceColour(performance) {
  if (performance === null || performance === undefined) return 'text-neutral-600'
  if (performance >= 100) return 'text-emerald-600'
  if (performance >= 80) return 'text-amber-600'
  return 'text-red-600'
}

/**
 * Single metric card component
 */
function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColour = 'text-neutral-500',
  valueColour = 'text-neutral-900',
  isLoading = false
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-10 w-10 rounded-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-500">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${valueColour}`}>{value}</p>
            {subtitle && (
              <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-2 rounded-full bg-neutral-100 ${iconColour}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * BreakevenMetrics Component
 *
 * @param {Object} props
 * @param {Object} props.data - Breakeven data from useBreakeven hook
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function BreakevenMetrics({
  data,
  isLoading = false,
  className = ''
}) {
  // Extract values from data
  const breakevenKg = data?.breakeven_kg ?? null
  const breakevenRevenue = data?.breakeven_revenue ?? null
  const safetyMargin = data?.safety_margin_percent ?? null
  const performance = data?.performance_percent ?? null
  const currentKg = data?.current_kg ?? null
  const currentRevenue = data?.current_revenue ?? null
  const status = data?.status

  // Handle insufficient data status
  const hasInsufficientData = status === 'zero_contribution' || status === 'negative_contribution'

  return (
    <div className={className}>
      {hasInsufficientData && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">
              {status === 'zero_contribution'
                ? 'Insufficient data to calculate breakeven'
                : 'Negative contribution margin detected'
              }
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {status === 'zero_contribution'
                ? 'No sales data or zero contribution in this period. Add sales data to see breakeven analysis.'
                : 'Current pricing does not cover variable costs. Each unit sold increases losses.'
              }
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Breakeven (kg)"
          value={formatKg(breakevenKg)}
          subtitle={currentKg !== null
            ? `Sold ${formatKg(currentKg)}`
            : 'To cover fixed costs'
          }
          icon={Target}
          iconColour="text-indigo-500"
          isLoading={isLoading}
        />

        <MetricCard
          title="Breakeven (GBP)"
          value={formatCurrency(breakevenRevenue)}
          subtitle={currentRevenue !== null
            ? `Turnover ${formatCurrency(currentRevenue)}`
            : 'Revenue required'
          }
          icon={PoundSterling}
          iconColour="text-emerald-500"
          isLoading={isLoading}
        />

        <MetricCard
          title="Distance"
          value={breakevenKg !== null && currentKg !== null
            ? `${currentKg >= breakevenKg ? '+' : ''}${formatKg(currentKg - breakevenKg)}`
            : 'N/A'
          }
          subtitle={breakevenKg !== null && currentKg !== null
            ? currentKg >= breakevenKg
              ? 'Above breakeven'
              : 'Below breakeven'
            : undefined
          }
          icon={Shield}
          iconColour={currentKg !== null && breakevenKg !== null
            ? currentKg >= breakevenKg ? 'text-emerald-500' : 'text-red-500'
            : 'text-neutral-500'
          }
          valueColour={currentKg !== null && breakevenKg !== null
            ? currentKg >= breakevenKg ? 'text-emerald-600' : 'text-red-600'
            : 'text-neutral-900'
          }
          isLoading={isLoading}
        />

        <MetricCard
          title="Performance"
          value={formatPercent(performance)}
          subtitle={performance !== null && performance >= 100
            ? 'Profitable'
            : performance !== null
              ? 'Below target'
              : undefined
          }
          icon={TrendingUp}
          iconColour={getPerformanceColour(performance)}
          valueColour={getPerformanceColour(performance)}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

/**
 * BreakevenMetrics Skeleton
 */
export function BreakevenMetricsSkeleton({ className = '' }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {[1, 2, 3, 4].map((i) => (
        <MetricCard key={i} isLoading={true} />
      ))}
    </div>
  )
}
